// src/components/DynamicPropertiesInput/DynamicPropertiesInput.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Stack, TextField, IconButton, Button, Chip, Divider, Typography, Tooltip
} from '@mui/material';
import { DeleteOutline, AddCircleOutline } from '@mui/icons-material';

const rowTemplate = (label = '', value = '') => ({ label, value });

/**
 * Componente controlado para manejar pares {label, value}:
 *  - Botón "Añadir propiedad"
 *  - Chips de plantillas para insertar filas preconfiguradas
 */
export default function DynamicPropertiesInput({
                                                   value = [],
                                                   onChange = () => {},
                                                   templates = [],
                                                   maxRows = 50
                                               }) {
    const [rows, setRows] = useState(Array.isArray(value) ? value : []);

    useEffect(() => {
        setRows(Array.isArray(value) ? value : []);
    }, [value]);

    useEffect(() => {
        onChange(rows);
    }, [rows, onChange]);

    const labelsUsadas = useMemo(() => new Set(rows.map(r => (r?.label ?? '').trim())), [rows]);

    const addRow = (presetLabel = '') => {
        if (rows.length >= maxRows) return;
        setRows(prev => [...prev, rowTemplate(presetLabel, '')]);
    };

    const addFromTemplate = (tpl) => {
        const label = String(tpl).trim();
        if (labelsUsadas.has(label)) return; // evita duplicados por plantilla
        addRow(label);
    };

    const updateRow = (idx, key, val) => {
        setRows(prev => prev.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
    };

    const removeRow = (idx) => {
        setRows(prev => prev.filter((_, i) => i !== idx));
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Plantillas rápidas
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {templates.map((tpl) => (
                    <Chip
                        key={tpl}
                        label={tpl}
                        variant={labelsUsadas.has(tpl) ? 'filled' : 'outlined'}
                        color={labelsUsadas.has(tpl) ? 'success' : 'default'}
                        onClick={() => addFromTemplate(tpl)}
                        sx={{ mb: 1 }}
                    />
                ))}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1.5}>
                {rows.map((row, idx) => (
                    <Stack
                        key={`${idx}-${row.label}`}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems="center"
                    >
                        <TextField
                            label="Propiedad (ej. Supervisor de ...)"
                            fullWidth
                            value={row.label}
                            onChange={(e) => updateRow(idx, 'label', e.target.value)}
                            placeholder="Nombre de la propiedad"
                        />
                        <TextField
                            label="Valor"
                            fullWidth
                            value={row.value}
                            onChange={(e) => updateRow(idx, 'value', e.target.value)}
                            placeholder="Ingrese el valor"
                        />
                        <Tooltip title="Eliminar fila">
                            <IconButton color="error" onClick={() => removeRow(idx)}>
                                <DeleteOutline />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ))}
            </Stack>

            <Box sx={{ mt: 2 }}>
                <Button
                    startIcon={<AddCircleOutline />}
                    variant="outlined"
                    onClick={() => addRow('')}
                    disabled={rows.length >= maxRows}
                >
                    Añadir propiedad
                </Button>
            </Box>
        </Box>
    );
}
